import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaExternalLinkAlt } from 'react-icons/fa';
import styled from 'styled-components';

import * as constants from '@/constants';
import { evaluatePasswordStrength } from '@/utils/evaluatePasswordStrength';
import { classifiedSort } from '@/utils/name';
import { PasswordField } from '@/components/shared/PasswordField';
import { PasswordPolicy } from '@/components/shared/PasswordPolicy';
import { Label } from '@/components/shared/Label';
import { SharedButton } from '@/components/shared/Button';
import { Input } from '@/components/shared/Input';

const strengthMeter = evaluatePasswordStrength(constants.PASSWORD_PATTERN);

export type Values = {
  firstName: string;
  lastName: string;
  kanaFirstName: string;
  kanaLastName: string;
  password: string;
};

type Props = {
  errors: Partial<Record<keyof Values, string>>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  dirty: boolean;
  isSubmitting: boolean;
  isDisable: boolean;
  touched: Partial<Record<keyof Values, boolean>>;
  values: Values;
};
export const FormContents: React.FC<Props> = ({
  errors,
  handleChange,
  handleSubmit,
  dirty,
  isSubmitting,
  isDisable,
  touched,
  values,
}) => {
  const { t } = useTranslation();

  return (
    <Styled>
      <form onSubmit={handleSubmit}>
        <div className="name-input">
          <label>
            {t('models.contact.personInCharge')}
            <FieldRow>
              {classifiedSort([
                <FieldCell key="first-name">
                  <Input
                    data-test="first-name"
                    className={
                      errors.firstName && touched.firstName ? 'is-error' : ''
                    }
                    placeholder={t('ui.expression.firstName')}
                    name="firstName"
                    onChange={handleChange}
                    value={values.firstName}
                  />
                  {touched.firstName && errors.firstName && (
                    <Label classNames={'parallel-elem'}>
                      {errors.firstName}
                    </Label>
                  )}
                </FieldCell>,
                <FieldCell key="last-name">
                  <Input
                    data-test="last-name"
                    className={
                      errors.lastName && touched.lastName ? 'is-error' : ''
                    }
                    placeholder={t('ui.expression.lastName')}
                    name="lastName"
                    onChange={handleChange}
                    value={values.lastName}
                  />
                  {touched.lastName && errors.lastName && (
                    <Label classNames={'parallel-elem'}>
                      {errors.lastName}
                    </Label>
                  )}
                </FieldCell>,
              ])}
            </FieldRow>
          </label>
        </div>
        <div className="hurikana-name">
          <label>
            {t('models.contact.personInCharge')}（{t('ui.expression.phonetic')}
            ）
            <FieldRow>
              {classifiedSort([
                <FieldCell key="kana-first-name">
                  <Input
                    data-test="kana-first-name"
                    className={
                      errors.kanaFirstName && touched.kanaFirstName
                        ? 'is-error'
                        : ''
                    }
                    placeholder={t('ui.expression.firstNamePhonetic')}
                    name="kanaFirstName"
                    onChange={handleChange}
                    value={values.kanaFirstName}
                  />
                  {touched.kanaFirstName && errors.kanaFirstName && (
                    <Label classNames={'parallel-elem'}>
                      {errors.kanaFirstName}
                    </Label>
                  )}
                </FieldCell>,
                <FieldCell key="kana-last-name">
                  <Input
                    data-test="kana-last-name"
                    className={
                      errors.kanaLastName && touched.kanaLastName
                        ? 'is-error'
                        : ''
                    }
                    placeholder={t('ui.expression.lastNamePhonetic')}
                    name="kanaLastName"
                    onChange={handleChange}
                    value={values.kanaLastName}
                  />
                  {touched.kanaLastName && errors.kanaLastName && (
                    <Label classNames={'parallel-elem'}>
                      {errors.kanaLastName}
                    </Label>
                  )}
                </FieldCell>,
              ])}
            </FieldRow>
          </label>
        </div>
        <PasswordField
          id={'password'}
          label={t('ui.password.label')}
          placeholder={t('ui.password.placeholder')}
          onChange={handleChange}
          strengthMeter={strengthMeter}
          value={values.password}
        />
        {touched.password && errors.password && (
          <div>
            <Label>{errors.password}</Label>
          </div>
        )}
        <PasswordPolicy />
        <div className="submit-button">
          <div className="admin-activation-terms">
            <a
              href="https://digital.globis.co.jp/term/1"
              rel="noopener noreferrer"
              target="_blank"
              className="with-icon"
            >
              {t('ui.terms.glp')}
              &nbsp;&nbsp;
              <FaExternalLinkAlt />
            </a>
          </div>
          <SharedButton
            dataTest="do-activation"
            disabled={
              !dirty ||
              isSubmitting ||
              isDisable ||
              strengthMeter(values.password) < 2
            }
            type="submit"
          >
            {t('ui.action.withAgreement.activate')}
          </SharedButton>
        </div>
      </form>
    </Styled>
  );
};

const Styled = styled.div`
  .address-form .button-group {
    margin: 2em 0 3em;
  }

  .name-input input,
  .hurikana-name input {
    padding: 10.31px 15.2px;
    line-height: 18.45px;
  }

  .hurikana-name {
    margin: 0 0 2.2em;
  }

  .admin-activation-terms {
    margin-bottom: 2em;
  }

  .with-icon {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  input::placeholder {
    color: rgba(191, 191, 191, 0.87);
  }
`;

const FieldRow = styled.div`
  display: flex;
  gap: 1em;
  width: 100%;
  margin-bottom: 1em;
`;

const FieldCell = styled.div`
  flex: 1;
  min-width: 0;

  > input {
    width: 100%;
  }
`;
